import {
  FileUpload,
  Select,
} from 'client/components/core/Sidebar/components/Settings/components'
import { WebsiteLink } from 'client/components/core/Sidebar/components/Settings/components/website-link'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { mimeTypes } from 'client/constants/editorSettings'
import { usePage } from 'client/store'
import { DataFile } from 'client/store/files/filesReducer'
import pageSelectors from 'client/store/page/pageSelectors'
import {
  ButtonActionTypeEnum,
  buttonActionsNames,
  getButtonActions,
} from 'common/enums/ButtonActionTypeEnum'
import { buttonLinkTypeNames } from 'common/enums/ButtonLinkEnum'
import EntityTypeEnum from 'common/enums/entityTypeEnum'
import { ButtonInterface } from 'common/types/entities/button-interface'
import { addUrlSchemaToTheLink } from 'common/utils/urlUtils'
import React from 'react'
import { CommonButtonSettings } from 'src/editor/shared/entities/common-button-settings'
import { CheckboxControl } from 'src/editor/shared/settings/checkbox-control'
import { TextInputControl } from 'src/editor/shared/settings/controls/text-input-control'
import { ButtonTypeGroup } from 'src/editor/shared/settings/groups/button-type-group'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { WebsiteButtonInterface } from 'src/shared/types/entities/website-button-interface'

interface WebsiteButtonSettingsProps {
  entity: WebsiteButtonInterface
  defaultEntity?: WebsiteButtonInterface
}

export const WebsiteButtonSettings = ({
  entity,
  defaultEntity,
}: WebsiteButtonSettingsProps) => {
  const { t } = useTypedTranslation()
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<ButtonInterface>()
  const popups = usePage(pageSelectors.getWebsiteGlobalOrPagePopupEntities)(
    entity,
  )

  const updateAction = (action: ButtonActionTypeEnum) => {
    const popup = action !== ButtonActionTypeEnum.ShowPopup ? '' : entity.popup

    const updatedEntity = {
      ...entity,
      action,
      popup,
    }

    const needResetFile =
      action === ButtonActionTypeEnum.DownloadFile &&
      entity.action !== ButtonActionTypeEnum.DownloadFile
    if (needResetFile) {
      delete updatedEntity.fileId
    }

    updateEntity(updatedEntity)
  }

  const removeFileId = () => {
    const updatedEntity = { ...entity }
    delete updatedEntity.fileId
    updateEntity(updatedEntity)
  }

  const updatePopup = (popupId: string) => {
    updateProp('popup')(popupId)
  }

  const toggleNoFollow = (value: boolean) => {
    let noFollow = ''
    if (value) {
      noFollow = 'nofollow'
    }
    updateProp('rel')(noFollow)
  }

  const popupChoiceList = popups.reduce(
    (popupChoices, popup) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
      ...popupChoices,
      [popup.id]:
        popup.type === EntityTypeEnum.Popup
          ? popup?.title
          : popup?.options?.title,
    }),
    {},
  )

  const pageButtonActions = [
    ButtonActionTypeEnum.OpenUrl,
    ButtonActionTypeEnum.WebsitePageLink,
    ButtonActionTypeEnum.ShowPopup,
    ButtonActionTypeEnum.DownloadFile,
  ]
  const availableButtonActions = pageButtonActions.reduce(
    (actions, action) => ({
      // biome-ignore lint/performance/noAccumulatingSpread: <explanation>
      ...actions,
      [action]: buttonActionsNames[action],
    }),
    {} as Record<ButtonActionTypeEnum, string>,
  )

  return (
    <>
      <ButtonTypeGroup entity={entity} />
      {!!pageButtonActions.length && (
        <Select
          update={updateAction}
          choiceList={availableButtonActions}
          labelText="entity_settings.button.action_label"
          value={entity.action}
        />
      )}
      {entity.action === ButtonActionTypeEnum.DownloadFile && (
        <FileUpload
          fileId={entity.fileId}
          afterUpload={(file: DataFile) => updateProp('fileId')(file.id)}
          removeFile={removeFileId}
          mimeTypes={mimeTypes.all}
        />
      )}
      {entity.action === ButtonActionTypeEnum.ShowPopup && (
        <Select
          labelText="Popup"
          update={updatePopup}
          choiceList={popupChoiceList}
          firstOption="entity_settings.button.choose_actions"
          value={entity.popup}
          skipTranslation
        />
      )}
      {entity.action === ButtonActionTypeEnum.OpenUrl && [
        <TextInputControl
          key="url"
          wrapperProps={{
            labelText: 'url',
          }}
          update={value => updateProp('linkUrl')(addUrlSchemaToTheLink(value))}
          placeholder={t('entity_settings.button.url_input.placeholder')}
          value={entity.linkUrl}
          removeWhitespaces
        />,
        <Select
          key="link-type"
          labelText="open in new window"
          update={updateProp('linkTarget')}
          choiceList={buttonLinkTypeNames}
          value={entity.linkTarget}
        />,
        <CheckboxControl
          key="noFollow"
          label="entity_settings.button.no_follow.label"
          tooltipText="entity_settings.button.no_follow.description"
          value={Boolean(entity.rel)}
          update={toggleNoFollow}
        />,
      ]}
      {entity.action === ButtonActionTypeEnum.WebsitePageLink && (
        <WebsiteLink
          link={entity.linkUrl || null}
          update={updateProp('linkUrl')}
          withoutWrapper={false}
          labelText="Select a website page to create a link"
        />
      )}

      <CommonButtonSettings entity={entity} defaultEntity={defaultEntity} />
    </>
  )
}
